import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { PageVisibilityProvider } from "@/lib/usePageVisibility";
import PageGuard from "@/components/PageGuard";

import Landing from "@/pages/Landing";
import Services from "@/pages/Services";
import About from "@/pages/About";
import InPersonTraining from "@/pages/InPersonTraining";
import ContactPage from "@/pages/ContactPage";
import Testimonials from "@/pages/Testimonials";
import FAQ from "@/pages/FAQ";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Book from "@/pages/Book";
import ClientPortal from "@/pages/ClientPortal";
import PaymentResult from "@/pages/PaymentResult";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import DataDeletion from "@/pages/DataDeletion";
import Waiver from "@/pages/Waiver";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import Meetups from "@/pages/Meetups";
import MeetupDetail from "@/pages/MeetupDetail";
import LiveChat from "@/components/LiveChat";
import NotFound from "@/pages/NotFound";

import AdminLayout from "@/pages/admin/AdminLayout";
import Overview from "@/pages/admin/Overview";
import Clients from "@/pages/admin/Clients";
import Leads from "@/pages/admin/Leads";
import Bookings from "@/pages/admin/Bookings";
import Payments from "@/pages/admin/Payments";
import Offers from "@/pages/admin/Offers";
import BlogPosts from "@/pages/admin/BlogPosts";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminHealth from "@/pages/admin/AdminHealth";
import EmailTemplates from "@/pages/admin/EmailTemplates";
import PageVisibility from "@/pages/admin/PageVisibility";
import NavOrder from "@/pages/admin/NavOrder";
import Stories from "@/pages/admin/Stories";
import ShareYourStory from "@/pages/ShareYourStory";
import StoryDetail from "@/pages/StoryDetail";
import RoninTaxonomy from "@/pages/admin/RoninTaxonomy";
import RoninLevelPicker from "@/pages/admin/RoninLevelPicker";
import RoninTimingPicker from "@/pages/admin/RoninTimingPicker";
import RoninSequencesAdmin from "@/pages/admin/RoninSequencesAdmin";
import RoninAnimations from "@/pages/admin/RoninAnimations";
import RoninAppPromo from "@/pages/admin/RoninAppPromo";
import RoninExercises from "@/pages/admin/RoninExercises";
import RoninPreviewAdmin from "@/pages/admin/RoninPreviewAdmin";
import AdminMeetups from "@/pages/admin/Meetups";
import MeetupEdit from "@/pages/admin/MeetupEdit";
import AdminInstagram from "@/pages/admin/Instagram";
import AdminEquipment from "@/pages/admin/Equipment";
import AdminLessons from "@/pages/admin/Lessons";
import Social from "@/pages/Social";
import Equipment from "@/pages/Equipment";
import Library from "@/pages/Library";
import LibraryDetail from "@/pages/LibraryDetail";
import Unsubscribe from "@/pages/Unsubscribe";
import RoninBox from "@/pages/RoninBox";
import RoninPromo from "@/pages/RoninPromo";
import RoundTimer from "@/pages/RoundTimer";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { LeadMagnetPopup } from "@/components/LeadMagnetPopup";
import { PopupManager } from "@/components/PopupManager";
import { ThemeApplier } from "@/lib/theme";
import AdminLibrary from "@/pages/admin/Library";
import AdminNewsletter from "@/pages/admin/Newsletter";
import AdminReminders from "@/pages/admin/Reminders";
import AdminPush from "@/pages/admin/Push";
import RoninBoxAdmin from "@/pages/admin/RoninBoxAdmin";
import RoninLeads from "@/pages/admin/RoninLeads";
import Popups from "@/pages/admin/Popups";
import Appearance from "@/pages/admin/Appearance";
import Coaching from "@/pages/Coaching";
import CoachingResult from "@/pages/CoachingResult";
import AdminCoaching from "@/pages/admin/Coaching";
import AdminPricing from "@/pages/admin/Pricing";
import AdminMarketing from "@/pages/admin/Marketing";
import AdminHomePage from "@/pages/admin/HomePage";
import AdminLlmsTxt from "@/pages/admin/LlmsTxt";
import PagesEditor from "@/pages/admin/PagesEditor";
import { AnalyticsScripts } from "@/components/AnalyticsScripts";
import Locations from "@/pages/Locations";
import LocationDetail from "@/pages/LocationDetail";
import Glossary from "@/pages/Glossary";
import GlossaryTerm from "@/pages/GlossaryTerm";

// Helper: wraps a public-facing page with the visibility guard so admin can
// flip it to Coming Soon / Hidden without touching code.
const Guarded = (pageKey, Element) => (
  <PageGuard pageKey={pageKey}>{Element}</PageGuard>
);

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PageVisibilityProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AnalyticsScripts />
            <Routes>
              <Route path="/" element={Guarded("home", <Landing />)} />
              <Route path="/services" element={Guarded("services", <Services />)} />
              <Route path="/about" element={Guarded("about", <About />)} />
              <Route path="/in-person-training" element={Guarded("in-person-training", <InPersonTraining />)} />
              <Route path="/contact" element={Guarded("contact", <ContactPage />)} />
              <Route path="/testimonials" element={Guarded("testimonials", <Testimonials />)} />
              <Route path="/faq" element={Guarded("faq", <FAQ />)} />
              <Route path="/book" element={Guarded("book", <Book />)} />
              <Route path="/blog" element={Guarded("blog", <BlogList />)} />
              <Route path="/blog/:slug" element={Guarded("blog", <BlogPost />)} />
              <Route path="/meetups" element={Guarded("meetups", <Meetups />)} />
              <Route path="/meetups/:slug" element={Guarded("meetups", <MeetupDetail />)} />
              <Route path="/social" element={Guarded("social", <Social />)} />
              <Route path="/equipment" element={Guarded("equipment", <Equipment />)} />
              <Route path="/library" element={Guarded("library", <Library />)} />
              <Route path="/library/:slug" element={Guarded("library", <LibraryDetail />)} />
              <Route path="/ronin-box" element={Guarded("ronin-box", <RoninPromo />)} />
              <Route path="/ronin-box/app" element={Guarded("ronin-box", <RoninBox />)} />
              <Route path="/round-timer" element={<RoundTimer />} />
              <Route path="/coaching" element={<Coaching />} />
              <Route path="/boxing-lessons" element={<Locations />} />
              <Route path="/boxing-lessons/:slug" element={<LocationDetail />} />
              <Route path="/boxing-glossary" element={<Glossary />} />
              <Route path="/boxing-glossary/:slug" element={<GlossaryTerm />} />
              <Route path="/coaching/success" element={<CoachingResult outcome="success" />} />
              <Route path="/coaching/cancel" element={<CoachingResult outcome="cancel" />} />
              <Route path="/share-your-story" element={Guarded("share-your-story", <ShareYourStory />)} />
              <Route path="/story/:id" element={Guarded("share-your-story", <StoryDetail />)} />

              {/* System pages — never gated */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/waiver" element={<Waiver />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/not-found" element={<NotFound />} />

              <Route path="/portal" element={<ProtectedRoute><ClientPortal /></ProtectedRoute>} />
              <Route path="/payment-success" element={<ProtectedRoute><PaymentResult outcome="success" /></ProtectedRoute>} />
              <Route path="/payment-cancel" element={<ProtectedRoute><PaymentResult outcome="cancel" /></ProtectedRoute>} />

              <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Overview />} />
                <Route path="home" element={<AdminHomePage />} />
                <Route path="pages" element={<PagesEditor />} />
                <Route path="llms" element={<AdminLlmsTxt />} />
                <Route path="clients" element={<Clients />} />
                <Route path="leads" element={<Leads />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="calendar" element={<AdminCalendar />} />
                <Route path="payments" element={<Payments />} />
                <Route path="offers" element={<Offers />} />
                <Route path="blog" element={<BlogPosts />} />
                <Route path="meetups" element={<AdminMeetups />} />
                <Route path="meetups/:meetup_id" element={<MeetupEdit />} />
                <Route path="instagram" element={<AdminInstagram />} />
                <Route path="equipment" element={<AdminEquipment />} />
                <Route path="lessons" element={<AdminLessons />} />
                <Route path="library" element={<AdminLibrary />} />
                <Route path="newsletter" element={<AdminNewsletter />} />
                <Route path="reminders" element={<AdminReminders />} />
                <Route path="push" element={<AdminPush />} />
                <Route path="coaching" element={<AdminCoaching />} />
                <Route path="pricing" element={<AdminPricing />} />
                <Route path="marketing" element={<AdminMarketing />} />
                <Route path="page-visibility" element={<PageVisibility />} />
                <Route path="navigation" element={<NavOrder />} />
                <Route path="email-templates" element={<EmailTemplates />} />
                <Route path="stories" element={<Stories />} />
                <Route path="ronin-box" element={<RoninBoxAdmin />} />
                <Route path="ronin-box/taxonomy" element={<RoninTaxonomy />} />
                <Route path="ronin-box/levels" element={<RoninLevelPicker />} />
                <Route path="ronin-box/timing" element={<RoninTimingPicker />} />
                <Route path="ronin-box/sequences" element={<RoninSequencesAdmin />} />
                <Route path="ronin-box/animations" element={<RoninAnimations />} />
                <Route path="ronin-box/app-promo" element={<RoninAppPromo />} />
                <Route path="ronin-box/exercises" element={<RoninExercises />} />
                <Route path="ronin-box/preview" element={<RoninPreviewAdmin />} />
                <Route path="ronin-leads" element={<RoninLeads />} />
                <Route path="popups" element={<Popups />} />
                <Route path="appearance" element={<Appearance />} />
                <Route path="email" element={<AdminEmail />} />
                <Route path="health" element={<AdminHealth />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <LeadMagnetPopup />
            <PopupManager />
            <ThemeApplier />
          </BrowserRouter>
        </PageVisibilityProvider>
        <Toaster position="top-right" theme="dark" />
        <LiveChat />
      </AuthProvider>
    </div>
  );
}
